/**
 * §30.D — Member activity drawer.
 * Right-slide capsule-clipped drawer showing summary KPIs, active work, and
 * the §25.B activity timeline for a single team member.
 *
 * No mutation controls — all edits route to Settings.
 *
 * Phase 8 · C-47 — The timeline + sparkline now go through
 * `teamMemberProfileService`, which gates every read on
 * `assertCanViewMemberProfile`. When the viewer is out of scope (peer
 * with `open_profiles=false`, manager looking at a non-report, …) we
 * render <AccessDeniedState> inside the drawer instead of leaking a
 * "Could not load activity" error. The old `source='mock'` branch is
 * gone; `/reports/team` is always live-data now.
 */

import { Link } from 'react-router-dom';
import {
  ExternalLink,
  Briefcase,
  Search,
  MailOpen,
  PlusCircle,
  Pencil,
  Trash2,
  Loader2,
  AlertTriangle,
  CheckSquare,
  Mail,
  CalendarCheck,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Drawer } from '@shared/components/ui/Drawer';
import { TableAvatar } from '@shared/components/ui/TableAvatar';
import { Sparkline } from '@shared/components/ui/Sparkline';
import { KpiCard } from '@modules/dashboard/components/KpiCard';
import { AccessDeniedState } from '@shared/components/ui/AccessDeniedState';
import { useAuth } from '@core/auth/useAuth';
import { queryKeys } from '@core/query/queryKeys';
import { viewerFromAuthUser, isRbacDenied } from '@core/auth/rbac';
import {
  teamMemberProfileService,
  type MemberActivityRow,
  type MemberThroughput30d,
} from '@core/supabase/services/teamMemberProfileService';
import type { TeamMember, TeamTimelineEvent } from '../types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function timelineDotClass(action: string): string {
  const a = action.toLowerCase();
  if (a === 'insert') return 'bg-emerald-500 ring-emerald-500/25';
  if (a === 'delete') return 'bg-red-500 ring-red-500/25';
  return 'bg-amber-500 ring-amber-500/25';
}

function verbForAction(action: string): string {
  const a = action.toLowerCase();
  if (a === 'insert') return 'created';
  if (a === 'delete') return 'deleted';
  return 'updated';
}

function ActionIcon({ action }: { action: string }) {
  const a = action.toLowerCase();
  if (a === 'insert')
    return <PlusCircle className="h-3.5 w-3.5 text-emerald-400 shrink-0" aria-hidden />;
  if (a === 'delete')
    return <Trash2 className="h-3.5 w-3.5 text-red-400 shrink-0" aria-hidden />;
  return <Pencil className="h-3.5 w-3.5 text-amber-400 shrink-0" aria-hidden />;
}

function workTypeIcon(type: TeamMember['activeWork'][number]['type']) {
  if (type === 'smart-match') return <Search className="h-3.5 w-3.5 shrink-0 vscode-text-tertiary" aria-hidden />;
  if (type === 'requisition') return <Briefcase className="h-3.5 w-3.5 shrink-0 vscode-text-tertiary" aria-hidden />;
  return <MailOpen className="h-3.5 w-3.5 shrink-0 vscode-text-tertiary" aria-hidden />;
}

function groupByDay(events: TeamTimelineEvent[]): { label: string; items: TeamTimelineEvent[] }[] {
  const map = new Map<string, TeamTimelineEvent[]>();
  for (const ev of events) {
    const d = new Date(ev.created_at);
    const key = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    let bucket = map.get(key);
    if (!bucket) { bucket = []; map.set(key, bucket); }
    bucket.push(ev);
  }
  return Array.from(map.entries()).map(([label, items]) => ({ label, items }));
}

function RolePill({ role }: { role: TeamMember['role'] }) {
  const cls =
    role === 'Admin'
      ? 'bg-violet-500/15 text-violet-700 dark:text-violet-300'
      : role === 'Viewer'
        ? 'bg-zinc-500/15 vscode-text-secondary'
        : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cls}`}>
      {role}
    </span>
  );
}

// ---------------------------------------------------------------------------
// API → UI mapping
// ---------------------------------------------------------------------------
function actionToTimeline(action: string): TeamTimelineEvent['action'] {
  const a = action.toLowerCase();
  if (a === 'insert') return 'insert';
  if (a === 'delete') return 'delete';
  return 'update';
}

function entityToTableName(entity: string): string {
  // Normalise entity_type values from ggj_b2b_object_history into the
  // plural nouns the timeline copy already uses ("candidates",
  // "applications", "emails", …).
  if (entity === 'email_thread') return 'emails';
  if (entity.endsWith('s')) return entity;
  return `${entity}s`;
}

function activityRowToTimeline(
  ev: MemberActivityRow,
  actorName: string,
): TeamTimelineEvent {
  return {
    id: ev.id,
    action: actionToTimeline(ev.action),
    table_name: entityToTableName(ev.entity_type),
    attribute_name: ev.attribute_name ?? undefined,
    old_value: ev.old_value ?? undefined,
    new_value: ev.new_value ?? undefined,
    created_at: ev.created_at,
    actor_name: actorName,
  };
}

/**
 * Squash the 14-day throughput matrix into a single series the
 * "Activity · last 7 days" capsule used to render from
 * reportsTeamService.getSparkline. We take the last 7 days and sum
 * across all four dimensions (candidates / interviews / emails /
 * offers) so the total matches the "N events" caption above.
 */
function throughputToSpark(tp: MemberThroughput30d | undefined): number[] {
  if (!tp) return new Array<number>(7).fill(0);
  const n = 7;
  const result = new Array<number>(n).fill(0);
  const offset = Math.max(0, tp.candidatesTouched.length - n);
  for (let i = 0; i < n; i++) {
    const src = offset + i;
    result[i] =
      (tp.candidatesTouched[src] ?? 0) +
      (tp.interviewsRun[src] ?? 0) +
      (tp.emailsSent[src] ?? 0) +
      (tp.offersIssued[src] ?? 0);
  }
  return result;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface MemberActivityDrawerProps {
  member: TeamMember | null;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function MemberActivityDrawer({
  member,
  onClose,
}: MemberActivityDrawerProps) {
  const open = member !== null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      side="right"
      panelMaxWidthPx={480}
      ariaLabel="Member activity"
      panelClassName="rounded-l-[10px]"
    >
      {member ? <DrawerBody member={member} onClose={onClose} /> : null}
    </Drawer>
  );
}

function DrawerBody({
  member,
  onClose,
}: {
  member: TeamMember;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const viewer = viewerFromAuthUser(user);
  const orgId = viewer.orgId ?? '';
  const viewerId = viewer.orgUserId ?? '';
  const enabled = Boolean(orgId && viewerId && member.id);

  const activityQuery = useQuery({
    queryKey: queryKeys.teamMemberProfile.activity(orgId, viewerId, member.id, 50),
    queryFn: () =>
      teamMemberProfileService.getActivity(
        { viewer, targetUserId: member.id },
        { limit: 50 },
      ),
    enabled,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const throughputQuery = useQuery({
    queryKey: queryKeys.teamMemberProfile.throughput(orgId, viewerId, member.id),
    queryFn: () =>
      teamMemberProfileService.getThroughput30d({ viewer, targetUserId: member.id }),
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const denied =
    isRbacDenied(activityQuery.error) || isRbacDenied(throughputQuery.error);

  const events: TeamTimelineEvent[] = (activityQuery.data ?? []).map((ev) =>
    activityRowToTimeline(ev, member.name),
  );
  const dayGroups = groupByDay(events);
  const sparkValues = throughputToSpark(throughputQuery.data);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {/* Header */}
      <header className="flex shrink-0 items-start gap-3 border-b vscode-border px-4 py-3">
        <TableAvatar name={member.name} size={36} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-[14px] font-semibold vscode-text-primary">{member.name}</p>
            <RolePill role={member.role} />
          </div>
          <p className="text-[12px] vscode-text-tertiary truncate">{member.email}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {!denied && (
            <Link
              to={`/team/${member.id}`}
              className="inline-flex items-center gap-1 rounded-[8px] border px-2.5 py-1 text-[12px] font-medium vscode-border vscode-text-secondary transition-colors hover:vscode-text-primary hover:vscode-bg-hover"
            >
              View full profile
              <ExternalLink className="h-3 w-3" aria-hidden />
            </Link>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 vscode-text-tertiary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
            aria-label="Close"
          >
            ×
          </button>
        </div>
      </header>

      {denied ? (
        <div className="flex-1 overflow-y-auto px-4 py-6">
          <AccessDeniedState
            message="You don't have access to this member's activity. Ask an admin or enable open profiles in Organisation settings."
            backHref="/reports/team"
            backLabel="Back to team activity"
          />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
          {/* §30.D.1 — Summary KPIs */}
          <section aria-labelledby="drawer-kpi-heading">
            <h2
              id="drawer-kpi-heading"
              className="mb-3 text-[11px] font-semibold uppercase tracking-wider vscode-text-tertiary"
            >
              Summary
            </h2>
            <div className="grid grid-cols-2 gap-2">
              <KpiCard
                kpiKey={`drawer-${member.id}-searches`}
                label="Active searches"
                icon={Search}
                value={member.activeSearches}
                enableFeedback={false}
              />
              <KpiCard
                kpiKey={`drawer-${member.id}-shortlisted`}
                label="Shortlisted"
                icon={CheckSquare}
                value={member.shortlisted}
                enableFeedback={false}
              />
              <KpiCard
                kpiKey={`drawer-${member.id}-outreach`}
                label="Outreach sent"
                icon={Mail}
                value={`${member.outreachSent} · ${member.outreachReplyRate}% replied`}
                enableFeedback={false}
              />
              <KpiCard
                kpiKey={`drawer-${member.id}-interviews`}
                label="Interviews held"
                icon={CalendarCheck}
                value={member.interviewsHeld}
                enableFeedback={false}
              />
            </div>

            {/* 7-day activity sparkline — derived from
                teamMemberProfileService.getThroughput30d. */}
            <div className="mt-3 flex items-center justify-between rounded-[8px] border vscode-border vscode-bg-secondary px-3 py-2">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider vscode-text-tertiary">
                  Activity · last 7 days
                </p>
                <p className="text-[12px] vscode-text-secondary">
                  {sparkValues.reduce((sum, v) => sum + v, 0)} events
                </p>
              </div>
              {throughputQuery.isLoading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin vscode-text-tertiary" aria-hidden />
              ) : (
                <Sparkline values={sparkValues} />
              )}
            </div>
          </section>

          {/* §30.D.2 — Active work (still sourced from the row the
              page passed in; live active-work needs a dedicated
              endpoint — tracked for a follow-up cluster). */}
          <section aria-labelledby="drawer-work-heading">
            <h2
              id="drawer-work-heading"
              className="mb-3 text-[11px] font-semibold uppercase tracking-wider vscode-text-tertiary"
            >
              Active work
            </h2>
            {member.activeWork.length === 0 ? (
              <p className="text-[12px] vscode-text-tertiary">No active work in this period.</p>
            ) : (
              <ul className="space-y-1.5" role="list">
                {member.activeWork.map((item) => (
                  <li
                    key={item.id}
                    className="flex items-center gap-2 rounded-[8px] border vscode-border px-3 py-2 vscode-bg-secondary"
                  >
                    {workTypeIcon(item.type)}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] vscode-text-primary">{item.title}</p>
                      <p className="text-[11px] vscode-text-tertiary">
                        Last touched {relativeTime(item.lastTouchedAt)}
                      </p>
                    </div>
                    <Link
                      to={item.href}
                      className="inline-flex h-6 shrink-0 items-center rounded-[6px] border px-2 text-[11px] font-medium vscode-border vscode-text-secondary transition-colors hover:vscode-text-primary hover:vscode-bg-hover"
                    >
                      Open →
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* §30.D.3 — Activity timeline (§25.B primitive reused) */}
          <section aria-labelledby="drawer-timeline-heading">
            <h2
              id="drawer-timeline-heading"
              className="mb-3 text-[11px] font-semibold uppercase tracking-wider vscode-text-tertiary"
            >
              Activity timeline
            </h2>

            {activityQuery.isLoading ? (
              <p className="text-[12px] vscode-text-tertiary inline-flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                Loading activity…
              </p>
            ) : activityQuery.isError ? (
              <p className="text-[12px] text-amber-700 dark:text-amber-300 inline-flex items-center gap-2">
                <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                Could not load activity.
              </p>
            ) : events.length === 0 ? (
              <p className="text-[12px] vscode-text-tertiary">No events recorded.</p>
            ) : (
              <div className="space-y-4">
                {dayGroups.map(({ label, items }) => (
                  <div key={label}>
                    <p className="mb-2 text-[11px] font-medium vscode-text-tertiary">{label}</p>

                    {/* §25.B vertical-line spine */}
                    <div className="relative pl-6">
                      <div
                        className="absolute left-[7px] top-2 w-0.5 vscode-bg-tertiary"
                        style={{ bottom: 8 }}
                        aria-hidden
                      />
                      {items.map((ev) => {
                        const verb = verbForAction(ev.action);
                        const obj = (ev.table_name ?? '').replace(/_/g, ' ');
                        const hasDiff = ev.attribute_name && (ev.old_value || ev.new_value);
                        return (
                          <div key={ev.id} className="relative mb-2.5 pl-5">
                            <div
                              className={`absolute -left-[1px] top-1.5 h-3 w-3 shrink-0 rounded-full ring-[3px] ring-[var(--vscode-bg-primary)] ${timelineDotClass(ev.action)}`}
                              aria-hidden
                            />
                            <div className="flex items-start gap-2 rounded-[8px] border px-3 py-2 vscode-border vscode-bg-secondary">
                              <ActionIcon action={ev.action} />
                              <div className="min-w-0 flex-1">
                                <p className="text-[12px] vscode-text-primary">
                                  <span className="font-medium">{ev.actor_name ?? member.name}</span>
                                  {' '}
                                  <span className="vscode-text-secondary">{verb}</span>
                                  {' '}
                                  <span className="font-mono text-[11px] vscode-text-secondary">{obj}</span>
                                </p>
                                {hasDiff && (
                                  <p className="mt-0.5 font-mono text-[11px] vscode-text-tertiary">
                                    {ev.attribute_name}:{' '}
                                    <span className="text-red-400 line-through">{ev.old_value ?? '—'}</span>
                                    {' → '}
                                    <span className="text-emerald-400">{ev.new_value ?? '—'}</span>
                                  </p>
                                )}
                              </div>
                              <time className="shrink-0 text-[11px] vscode-text-tertiary">
                                {relativeTime(ev.created_at)}
                              </time>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
